import {
  Box,
  Divider,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
  useTheme,
} from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import GearIcon from '@watershed/icons/components/Gear';
import ListIcon from '@watershed/icons/components/List';
import LogOutIcon from '@watershed/icons/components/LogOut';
import {
  routeForAuditLogs,
  routeForLearningHub,
  routeForOrganizationMembers,
} from '@watershed/shared-universal/dashboardRoutes';
import useToggle from '@watershed/ui-core/hooks/useToggle';
import Button from '@watershed/ui-core/components/Button';
import UnstyledLink from '@watershed/ui-core/components/UnstyledLink';
import { useContext, useState } from 'react';
import useLogout from '@watershed/shared-frontend/hooks/useLogout';
import { UserContext } from '../../utils/UserContext';
import { TestIds } from '@watershed/shared-universal/utils/testUtils';
import FriendsIcon from '@watershed/icons/components/Friends';
import SwitchOrganizationDialog from './SwitchOrgDialog';
import usePaletteUtils from '@watershed/ui-core/hooks/usePaletteUtils';
import { getSharedSidebarStyles } from './variants/sharedSidebarStyles';
import { useRouter } from 'next/router';
import { HelpNavButton } from '../discussions/NavHelpButton';
import LightBulbIcon from '@watershed/icons/components/LightBulb';
import { learningHubBaseUrlForViewMode } from '@watershed/shared-universal/learningHubRoutes';
import { GQFlags } from '@watershed/shared-universal/generated/graphql';
import { useFeatureFlag } from '../../utils/FeatureFlag';

const useStyles = makeStyles(() =>
  createStyles({
    userBarMenu: {
      width: 228,
      top: 'unset !important',
      bottom: 56,
    },
  })
);

export default function UserBar({
  isOnFinancePage,
  appearsCollapsed,
  isAdmin,
}: {
  isOnFinancePage: boolean;
  appearsCollapsed: boolean;
  isAdmin: boolean;
}) {
  const assetManagerServiceCloudSupport = useFeatureFlag(
    GQFlags.AssetManagerServiceCloudSupport
  );
  const showSupportOption = !isOnFinancePage || assetManagerServiceCloudSupport;
  const { pathname } = useRouter();
  const paletteUtils = usePaletteUtils();
  const [anchorEl, setAnchorEl] = useState<null | HTMLButtonElement>(null);
  const classes = useStyles();
  const userContext = useContext(UserContext);
  const onLogout = useLogout();
  const [switchOrgDialogOpen, toggleSwitchOrgDialog] = useToggle();

  const { COLORS } = getSharedSidebarStyles(useTheme());

  return (
    <Box
      sx={{
        margin: 'auto 0 0',
        backgroundColor: COLORS.BACKGROUND,
        color: COLORS.TEXT_PRIMARY,
      }}
    >
      <Divider sx={{ borderColor: (theme) => theme.palette.grey80 }} />
      {showSupportOption && <HelpNavButton isOnFinancePage={isOnFinancePage} />}
      {!showSupportOption && (
        <Button
          variant="text"
          startIcon={<LightBulbIcon />}
          href={routeForLearningHub({
            from: 'Sidebar',
            viewMode: isOnFinancePage ? 'finance' : 'regular',
          })}
          sx={{
            width: '100%',
            height: '48px',
            justifyContent: 'flex-start',
            textAlign: 'left',
            color: COLORS.TEXT_PRIMARY,
            borderLeft: '4px solid transparent',
            borderRadius: 0,
            paddingLeft: '14px', // Match alignment of gear icon
            paddingRight: '18px',

            '&:hover, &:focus': {
              backgroundColor: COLORS.BACKGROUND_ACTIVE,
            },
            '&:focus': {
              boxShadow: paletteUtils.boxShadowFocus,
            },
            '&[aria-current="page"]': {
              color: COLORS.TEXT_PRIMARY,
              backgroundColor: COLORS.BACKGROUND_ACTIVE,
              borderColor: COLORS.BORDER_ACTIVE,
            },
            '& .MuiButton-startIcon': {
              marginLeft: 0,
              marginRight: 0,
              '& svg': {
                width: 20,
                height: 20,
              },
            },
          }}
          aria-current={
            pathname.includes(
              learningHubBaseUrlForViewMode(
                isOnFinancePage ? 'finance' : 'regular'
              )
            )
              ? 'page'
              : undefined
          }
        >
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              paddingY: 2,
              marginLeft: '8px', // Match alignment of navbar items
            }}
          >
            Learning hub
          </Box>
        </Button>
      )}

      <Button
        data-test={TestIds.UserBarButton}
        variant="text"
        startIcon={
          <GearIcon
            color={COLORS.TEXT_PRIMARY}
            style={{
              transform: anchorEl === null ? undefined : 'rotate(30deg)',
            }}
          />
        }
        aria-controls="user-menu"
        aria-haspopup="true"
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          width: '100%',
          justifyContent: 'flex-start',
          textAlign: 'left',
          color: COLORS.TEXT_PRIMARY,
          height: 'auto', // Undo default 32px height
          borderRadius: 0,
          paddingX: '18px', // Match alignment of navbar icon

          '&:hover, &:focus': {
            backgroundColor: COLORS.BACKGROUND_ACTIVE,
          },

          '& .MuiButton-startIcon': {
            margin: 0,
            '& svg': {
              width: 20,
              height: 20,
            },
            pb: 2,
          },
          '&:focus': {
            boxShadow: paletteUtils.boxShadowFocus,
          },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            paddingY: 2,
            marginLeft: '8px', // Match alignment of navbar items
          }}
        >
          {userContext.userName}
          <br />
          <Typography
            variant="caption"
            sx={{
              color: COLORS.TEXT_PRIMARY,
            }}
          >
            {userContext.orgName}
          </Typography>
        </Box>
      </Button>
      <Menu
        id="user-menu"
        anchorEl={anchorEl}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        PaperProps={{
          className: classes.userBarMenu,
        }}
      >
        {isAdmin && (
          <UnstyledLink href={routeForOrganizationMembers()}>
            <MenuItem
              data-test={TestIds.OrganizationSettingsItem}
              onClick={() => {
                setAnchorEl(null);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                }}
              >
                <GearIcon />
              </ListItemIcon>
              Organization settings
            </MenuItem>
          </UnstyledLink>
        )}
        {isAdmin && (
          <UnstyledLink href={routeForAuditLogs()}>
            <MenuItem
              onClick={() => {
                setAnchorEl(null);
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 36,
                }}
              >
                <ListIcon />
              </ListItemIcon>
              Audit log
            </MenuItem>
          </UnstyledLink>
        )}
        {userContext.accessibleOrgs.length > 1 && (
          <MenuItem
            onClick={() => {
              toggleSwitchOrgDialog();
              setAnchorEl(null);
            }}
            disabled={userContext.loginAsUser !== null}
          >
            <ListItemIcon
              sx={{
                minWidth: 36,
              }}
            >
              <FriendsIcon />
            </ListItemIcon>
            Switch organization
          </MenuItem>
        )}
        <MenuItem
          component="button"
          onClick={() => {
            // TODO: URGENT Please fix this by await-ing or void-ing this line.
            // eslint-disable-next-line @typescript-eslint/no-floating-promises
            onLogout();
            setAnchorEl(null);
          }}
          sx={{
            letterSpacing: 'inherit',
            width: '100% !important',
          }}
          data-test={TestIds.LogoutButton}
        >
          <ListItemIcon
            sx={{
              minWidth: 36,
            }}
          >
            <LogOutIcon />
          </ListItemIcon>
          Sign out…
        </MenuItem>
      </Menu>
      <SwitchOrganizationDialog
        open={switchOrgDialogOpen}
        onClose={toggleSwitchOrgDialog}
      />
    </Box>
  );
}
